import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Button,
  Modal,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  Tabs,
  Tab,
  Box,
  Tooltip,
} from "@mui/material";
import moment from "moment";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ErrorModal from "./ErrorModal";
import TabPanel from "./TabPanel";
import { getImportTabs } from "./importTabs";
import { getSystemFormattedTimestamp } from "../../utils/dateFormatted";
import { clearImportError } from "../../redux/donorSlice";
import { logger } from "../../utils/logger";

const ImportDataList = () => {
  const {
    duplicateDonors,
    duplicate,
    DonorsWithMissingId,
    DonorsWithMissingField,
    loading,
    error,validationError,
  } = useSelector((state) => state.donor);
  const { prasadMissingDonors, prasadIncompleteDonors } = useSelector(
    (state) => state.importData,
  );
  const dispatch = useDispatch();
  const [formattedDuplicates, setFormattedDuplicates] = useState([]);
  const [duplicateNewDonor, setDuplicateNewDonor] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [formattedIncomplete, setFormattedIncomplete] = useState([]);
  const [DonorIdMissing, setDonorIdMissing] = useState([]);
  const [DonorFieldsMissing, setDonorFieldsMissing] = useState([]);
  const [formattedPrasadMissing, setFormattedPrasadMissing] = useState([]);
  const [formattedPrasadIncomplete, setFormattedPrasadIncomplete] = useState(
    [],
  );
  const [tabIndex, setTabIndex] = useState(0);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const { user } = useSelector((state) => state.auth);

  // Grid refs and APIs
  const duplicateGridRef = useRef(null);
  const duplicateDonorGridRef = useRef(null);
  const incompleteGridRef = useRef(null);
  const missingFieldGridRef = useRef(null);
  const donorIdMissingGridRef = useRef(null);
  const prasadMissingGridRef = useRef(null);
  const prasadIncompleteGridRef = useRef(null);
const validationErrorGridRef = useRef(null);
const [validationErrorGridApi, setValidationErrorGridApi] = useState(null);
  const [duplicateGridApi, setDuplicateGridApi] = useState(null);
  const [duplicateDonorGridApi, setDuplicateDonorGridApi] = useState(null);
  const [missingFieldGridApi, setMissingFieldGridApi] = useState(null);
  const [donorIdMissingGridApi, setDonorIdMissingGridApi] = useState(null);
  const [prasadMissingGridApi, setPrasadMissingGridApi] = useState(null);
  const [prasadIncompleteGridApi, setPrasadIncompleteGridApi] = useState(null);

  // Grid ready handlers
  const gridReadyHandlers = {
    onDuplicateGridReady: (params) => {
      setDuplicateGridApi(params.api);
    },
    onDuplicateDonorGridReady: (params) => {
      setDuplicateDonorGridApi(params.api);
    },
    onDonorMissingFieldGridReady: (params) => {
      setMissingFieldGridApi(params.api);
    },
    onDonorIdMissingGridReady: (params) => {
      setDonorIdMissingGridApi(params.api);
    },
    onPrasadMissingGridReady: (params) => {
      setPrasadMissingGridApi(params.api);
    },
    onPrasadIncompleteGridReady: (params) => {
      setPrasadIncompleteGridApi(params.api);
    },
    onValidationErrorGridReady: (params) => {
  setValidationErrorGridApi(params.api);
},
  };
  // Format Excel date
  const formatExcelDate = (excelDate) => {
    if (!excelDate || typeof excelDate !== "number") return excelDate;
    const jsDate = new Date((excelDate - 25569) * 86400 * 1000);
    return moment(jsDate).format("DD-MM-YYYY");
  };

  const handleCloseErrorModal = () => {
    setOpenErrorModal(false);
    dispatch(clearImportError());
  };

  useEffect(() => {
    if (error) {
      setOpenErrorModal(true);
    }
  }, [error]);

  // Data formatting effects
  useEffect(() => {
    if (duplicateDonors?.length > 0) {
      const cleaned = duplicateDonors.map((donor) => ({
        ID: donor["Donor ID"] || "-",
        BookNo: donor["Book No"]?.toString().trim() || "-",
        ReceiptNo: donor["Receipt No"] || "-",
        ReceiptDate: formatExcelDate(donor["Receipt Date"]) || "-",
        PayMode: donor["Pay Mode"] || "-",
        Amount: donor.Amount || "-",
        BankName: donor["Bank Name"] || "-",
        InstrumentNo: donor["Instrument No"] || "-",
        InstrumentDate: formatExcelDate(donor["Instrument Date"]) || "-",
        PayPurpose: donor["Pay Purpose"] || "-",
        Mobile1: donor["Mobile1"] ? donor["Mobile1"].toString().trim() : "-",
        Status: donor.Status,
        Remarks: donor.remarks || "-",
      }));
      setFormattedDuplicates(cleaned);
      if (cleaned.length > 0) {
        logger.warn(
          "Duplicate Donation records found in new and existing donor import",
          { username: user?.username, role: user?.role, count: cleaned.length },
        );
      }
    } else {
      setFormattedDuplicates([]);
    }
  }, [duplicateDonors]);

  useEffect(() => {
    if (duplicate?.length > 0) {
      const cleaned = duplicate.map((donor) => ({
        Name: donor["First Name"] || "-",
        Address: donor.Address || "-",
        Address1: donor.Address1 || "-",
        City: donor.City || "-",
        Pincode: donor.Pincode || "-",
        Mobile1: donor.Mobile1 || "-",
        Mobile2: donor.Mobile2 || "-",
        MailId: donor.MailId || "-",
        BookNo: donor["Book No"] || "-",
        ReceiptNo: donor["Receipt No"] || "-",
        ReceiptDate: formatExcelDate(donor["Receipt Date"]) || "-",
        Amount: donor.Amount || "-",
        PayMode: donor["Pay Mode"] || "-",
        BankName: donor["Bank Name"] || "-",
        InstrumentNo: donor["Instrument No"] || "-",
        InstrumentDate: formatExcelDate(donor["Instrument Date"]) || "-",
        PayPurpose: donor["Pay Purpose"] || "-",
        Status: donor.Status,
        Remarks: donor.remarks || "-",
      }));
      setDuplicateNewDonor(cleaned);
      if (cleaned.length > 0) {
        logger.warn(
          "Duplicate donor records found in new and existing donor import",
          { username: user?.username, role: user?.role, count: cleaned.length },
        );
      }
    } else {
      setDuplicateNewDonor([]);
    }
  }, [duplicate]);

  useEffect(() => {
    if (DonorsWithMissingId?.length > 0) {
      const formatted = DonorsWithMissingId.map((donor) => ({
        index: donor.rowIndex || "-",
        ID: donor["Donor ID"] || "-",
        BookNo: donor["Book No"]?.toString().trim() || "-",
        ReceiptNo: donor["Receipt No"] || "-",
        ReceiptDate: formatExcelDate(donor["Receipt Date"]) || "-",
        PayMode: donor["Pay Mode"] || "-",
        Amount: donor.Amount || "-",
        BankName: donor["Bank Name"] || "-",
        InstrumentNo: donor["Instrument No"] || "-",
        InstrumentDate: formatExcelDate(donor["Instrument Date"]) || "-",
        PayPurpose: donor["Pay Purpose"] || "-",
        Mobile1: donor.Mobile1 || "-",
        Remarks: donor.remarks || "-",
      }));
      setDonorIdMissing(formatted);
      if (formatted.length > 0) {
        logger.warn(
          "DonorId missing/ Donor not found records found in new and existiing donor import",
          {
            username: user?.username,
            role: user?.role,
            count: formatted.length,
          },
        );
      }
    } else {
      setDonorIdMissing([]);
    }
  }, [DonorsWithMissingId]);

  useEffect(() => {
    if (DonorsWithMissingField?.length > 0) {
      const formatted = DonorsWithMissingField.map((donor) => ({
        index: donor.rowIndex || "-",
        Name: donor["First Name"] || "-",
        Address: donor.Address || "-",
        Address1: donor.Address1 || "-",
        City: donor.City || "-",
        Pincode: donor.Pincode || "-",
        BookNo: donor["Book No"] || "-",
        ReceiptNo: donor["Receipt No"] || "-",
        ReceiptDate: formatExcelDate(donor["Receipt Date"]) || "-",
        PayMode: donor["Pay Mode"] || "-",
        Amount: donor.Amount || "-",
        BankName: donor["Bank Name"] || "-",
        InstrumentNo: donor["Instrument No"] || "-",
        InstrumentDate: formatExcelDate(donor["Instrument Date"]) || "-",
        PayPurpose: donor["Pay Purpose"] || "-",
        Mobile1: donor.Mobile1 || "-",
        Remarks: donor.remarks || "-",
      }));
      setDonorFieldsMissing(formatted);
      if (formatted.length > 0) {
        logger.warn(
          "Donation fields missing records found in new and existiing donor import",
          {
            username: user?.username,
            role: user?.role,
            count: formatted.length,
          },
        );
      }
    } else {
      setDonorFieldsMissing([]);
    }
  }, [DonorsWithMissingField]);

  useEffect(() => {
    if (prasadMissingDonors?.length > 0) {
      setFormattedPrasadMissing(
        prasadMissingDonors.map((id, index) => ({
          index: index + 1,
          donorId: id,
          remarks: "Donor not found",
        })),
      );
    } else {
      setFormattedPrasadMissing([]);
    }
  }, [prasadMissingDonors]);

  useEffect(() => {
    if (prasadIncompleteDonors?.length > 0) {
      setFormattedPrasadIncomplete(
        prasadIncompleteDonors.map((id, index) => ({
          index: index + 1,
          donorId: id,
          remarks: "Donor status is Incomplete",
        })),
      );
    } else {
      setFormattedPrasadIncomplete([]);
    }
  }, [prasadIncompleteDonors]);

  // Export handlers
  const exportHandlers = {
    exportDuplicateToExcel: () => {
      const fileName = `Already_uploaded_donations${getSystemFormattedTimestamp()}.csv`;
      duplicateGridApi?.exportDataAsCsv({ fileName });
      logger.info("Exported duplicate donations CSV", {
        username: user?.username,
        role: user?.role,
        fileName,
      });
    },
    exportDuplicateDonorToExcel: () => {
      const fileName = `Already_uploaded_donors${getSystemFormattedTimestamp()}.csv`;
      duplicateDonorGridApi?.exportDataAsCsv({ fileName });
      logger.info("Exported duplicate donors CSV", {
        username: user?.username,
        role: user?.role,
        fileName,
      });
    },
    exportDonorMissingFieldToExcel: () => {
      const fileName = `Missing_Donation_Field_in_New_Donors${getSystemFormattedTimestamp()}.csv`;
      missingFieldGridApi?.exportDataAsCsv({ fileName });
      logger.info("Exported donors with missing donation fields CSV", {
        username: user?.username,
        role: user?.role,
        fileName,
      });
    },
    exportDonorIdMissingToExcel: () => {
      const fileName = `Missing_Fields_in_Existing_Donors${getSystemFormattedTimestamp()}.csv`;
      donorIdMissingGridApi?.exportDataAsCsv({ fileName });
      logger.info("Exported donors with missing IDs CSV", {
        username: user?.username,
        role: user?.role,
        fileName,
      });
    },
    exportPrasadMissingToExcel: () => {
      const fileName = `Prasad_Missing_Donors${getSystemFormattedTimestamp()}.csv`;
      prasadMissingGridApi?.exportDataAsCsv({ fileName });
      logger.info("Exported Prasad missing donors CSV", {
        username: user?.username,
        role: user?.role,
        fileName,
      });
    },
    exportPrasadIncompleteToExcel: () => {
      const fileName = `Prasad_Incomplete_Donors${getSystemFormattedTimestamp()}.csv`;
      prasadIncompleteGridApi?.exportDataAsCsv({ fileName });
      logger.info("Exported Prasad incomplete donors CSV", {
        username: user?.username,
        role: user?.role,
        fileName,
      });
    },
    exportValidationErrorToExcel: () => {
  const fileName = `Book_Receipt_Mismatch_${getSystemFormattedTimestamp()}.csv`;
  validationErrorGridApi?.exportDataAsCsv({ fileName });
  logger.info("Exported Book/Receipt mismatch CSV", {
    username: user?.username,
    role: user?.role,
    fileName,
  });
},
  };

  const gridRefs = {
    duplicateGridRef,
    duplicateDonorGridRef,
    incompleteGridRef,
    missingFieldGridRef,
    donorIdMissingGridRef,
    prasadMissingGridRef,
    prasadIncompleteGridRef,
     validationErrorGridRef,
  };

  // Tab and panel data
  const tabs = getImportTabs({
    formattedDuplicates,
    duplicateNewDonor,
    formattedIncomplete,
    DonorFieldsMissing,
    DonorIdMissing,
    formattedPrasadMissing,
    formattedPrasadIncomplete,
    gridRefs,
    gridReadyHandlers,
    exportHandlers,
      validationErrors,
    user,
  });
useEffect(() => {
  if (validationError?.length > 0) {
    const formatted = validationError.map((row) => ({
      index: row.rowIndex || "-",
      ID: row["Donor ID"] || "-",
      BookNo: row["Book No"]?.toString().trim() || "-",
      ReceiptNo: row["Receipt No"] || "-",
      Remarks: (row.remarks || "-").replace("Error List:", "").trim(),
    }));
    setValidationErrors(formatted);
  } else {
    setValidationErrors([]);
  }
}, [validationError]);
  // Only show tabs/panels with data
  const visibleTabs = tabs.filter((tab) => tab.show);

  // If tabIndex is out of bounds (e.g., after filtering), reset to 0
  useEffect(() => {
    if (tabIndex >= visibleTabs.length) setTabIndex(0);
  }, [visibleTabs.length, tabIndex]);

  return (
    <>
      {/* Loading Modal */}
      <Modal
        open={loading}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Modal>

      {/* Error Button */}
      {error && (
        <Tooltip title="Error while importing data">
          <Button
            onClick={() => setOpenErrorModal(true)}
            style={{ color: "red" }}
            startIcon={<ErrorOutlineIcon fontSize="large" />}
          >
            Error
          </Button>
        </Tooltip>
      )}

      <ErrorModal
        open={openErrorModal}
        onClose={handleCloseErrorModal}
        error={error?.message || error}
      />

      {/* Main Card */}
      {visibleTabs.length > 0 && (
        <Card sx={{ width: "100%", margin: "auto", mt: 3, boxShadow: 3 }}>
          <CardHeader title="Import Summary" />
          <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
            <Tabs
              value={tabIndex}
              onChange={(e, newIndex) => {
                setTabIndex(newIndex);
                visibleTabs[newIndex]?.log?.();
              }}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              aria-label="import summary tabs"
            >
              {visibleTabs.map((tab, idx) => (
                <Tab key={tab.label} label={tab.label} />
              ))}
            </Tabs>
          </Box>
          <CardContent>
            {visibleTabs.map((tab, idx) => (
              <TabPanel key={tab.label} value={tabIndex} index={idx}>
                {tab.panel}
              </TabPanel>
            ))}
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default ImportDataList;
